import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { MetricData } from './PerformanceMetricsChart';
import { IndustryBenchmark } from '../../services/comparisonService';
import { tailwindColors, getPerformanceColor } from '../../utils/colorUtils';

// Register required Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface PerformanceRangeChartProps {
  metrics: MetricData[];
  industryBenchmarks?: IndustryBenchmark[];
  height?: number;
  // Hide the legend in compact contexts (e.g. dashboard mini-cards) where the
  // legend rows would overlap the bar at small heights and the same legend
  // would otherwise repeat across every card.
  showLegend?: boolean;
}

const PerformanceRangeChart: React.FC<PerformanceRangeChartProps> = ({
  metrics,
  industryBenchmarks = [],
  height = 400,
  showLegend = true,
}) => {
  // Format currency value for display
  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  // Format value based on whether it's monetary and its magnitude
  const formatValue = (value: number, unit: string, isMonetary: boolean): string => {
    if (isMonetary) {
      return formatCurrency(value);
    }
    
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M ${unit}`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K ${unit}`;
    }
    
    return `${value.toFixed(2)} ${unit}`;
  };

  // Normalize values to percentile positions for better visual comparison
  const normalizeToPercentile = (value: number, min: number, max: number): number => {
    if (max === min) return 50; // If no range, place at middle
    const normalized = ((value - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, normalized));
  };

  // Prepare data for horizontal bar chart using normalized percentile positions
  const chartData = {
    labels: metrics.map(metric => metric.metricName),
    datasets: [
      // Industry Range (Background) - Full width from 0 to 100
      {
        label: 'Industry Range',
        data: metrics.map(() => 100), // Full range
        backgroundColor: 'rgba(200, 200, 200, 0.3)',
        borderColor: 'rgba(200, 200, 200, 0.5)',
        borderWidth: 1,
        barThickness: 30,
        barPercentage: 1,
        categoryPercentage: 0.8,
        base: metrics.map(() => 0) // Start from 0
      },
      // Industry Average (Line) - Positioned at 50th percentile
      {
        label: 'Industry Average',
        data: metrics.map(metric => {
          const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
          if (benchmark) {
            return normalizeToPercentile(
              benchmark.nationalAverage,
              benchmark.industryMin,
              benchmark.industryMax
            );
          }
          return 50; // Default to middle if no benchmark
        }),
        backgroundColor: 'rgba(100, 100, 100, 0.8)',
        borderColor: 'rgba(100, 100, 100, 1)',
        borderWidth: 2,
        barThickness: 15, // Match the user value bar thickness
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
      // Top Performer Threshold (Line)
      {
        label: 'Top Performer Threshold',
        data: metrics.map(metric => {
          const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
          if (benchmark && benchmark.topPerformerThreshold) {
            return normalizeToPercentile(
              benchmark.topPerformerThreshold,
              benchmark.industryMin,
              benchmark.industryMax
            );
          }
          return null;
        }),
        backgroundColor: 'rgba(52, 211, 153, 0.8)',
        borderColor: 'rgba(52, 211, 153, 1)',
        borderWidth: 2,
        barThickness: 15, // Match the user value bar thickness
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
      // User Value (Main Bar) - Positioned based on actual percentile rank
      {
        label: 'Your Property',
        data: metrics.map(metric => metric.percentileRank),
        backgroundColor: metrics.map(metric => {
          const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
          const topThreshold = benchmark?.topPerformerThreshold;
          
          // Determine if this is a top performing metric
          const isTop = topThreshold && 
            ((metric.desiredTrend === 'up' && metric.userValue >= topThreshold) || 
             (metric.desiredTrend === 'down' && metric.userValue <= topThreshold));
          
          // Use utility function to get color based on percentile rank
          const isReversed = metric.desiredTrend === 'down';
          if (isTop) {
            return getPerformanceColor(95, isReversed, 0.8);
          } else {
            return getPerformanceColor(metric.percentileRank, isReversed, 0.8);
          }
        }),
        borderColor: metrics.map(metric => {
          const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
          const topThreshold = benchmark?.topPerformerThreshold;
          
          const isTop = topThreshold && 
            ((metric.desiredTrend === 'up' && metric.userValue >= topThreshold) || 
             (metric.desiredTrend === 'down' && metric.userValue <= topThreshold));
          
          if (isTop) {
            return 'rgba(52, 211, 153, 1)';
          } else if (metric.percentileRank >= 75 || (metric.desiredTrend === 'down' && metric.percentileRank <= 25)) {
            return 'rgba(59, 130, 246, 1)';
          } else if (metric.percentileRank >= 50 || (metric.desiredTrend === 'down' && metric.percentileRank <= 50)) {
            return 'rgba(251, 191, 36, 1)';
          } else {
            return 'rgba(239, 68, 68, 1)';
          }
        }),
        borderWidth: 1,
        barThickness: 15,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      }
    ],
  };

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
        beginAtZero: true,
        max: 100, // Set max to 100 for percentile scale
        grid: {
          display: true,
          drawBorder: true,
        },
        ticks: {
          stepSize: 25, // Show ticks at 0, 25, 50, 75, 100
          callback: function(value: any) {
            // Show percentile labels
            if (value === 0) return '0th (Min)';
            if (value === 25) return '25th';
            if (value === 50) return '50th (Avg)';
            if (value === 75) return '75th';
            if (value === 100) return '100th (Max)';
            return `${value}th`;
          }
        }
      },
      y: {
        stacked: false,
        grid: {
          display: false,
          drawBorder: false,
        }
      }
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom' as const,
        labels: {
          boxWidth: 12,
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (tooltipItems: any) => {
            const index = tooltipItems[0].dataIndex;
            const metric = metrics[index];
            return metric ? metric.metricName : '';
          },
          label: (context: any) => {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            const metric = metrics[index];

            if (!metric) return '';

            if (datasetIndex === 3) { // Your Property dataset
              const actualValue = metric.userValue;
              const formattedValue = metric.isMonetary
                ? formatCurrency(actualValue)
                : `${actualValue.toLocaleString()}${metric.unit ? ` ${metric.unit}` : ''}`;

              const percentileText = metric.desiredTrend === 'down'
                ? `${(100 - metric.percentileRank).toFixed(0)}th percentile (lower is better)`
                : `${metric.percentileRank.toFixed(0)}th percentile`;

              const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
              const topThreshold = benchmark?.topPerformerThreshold;
              const isTop = topThreshold &&
                ((metric.desiredTrend === 'up' && metric.userValue >= topThreshold) ||
                (metric.desiredTrend === 'down' && metric.userValue <= topThreshold));

              return [
                `${context.dataset.label}: ${formattedValue}`,
                `Performance: ${percentileText}`,
                isTop ? '✨ Top Performer' : ''
              ].filter(Boolean);
            } else if (datasetIndex === 0) { // Industry Range
              const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
              const minValue = benchmark ? benchmark.industryMin : metric.percentile25;
              const maxValue = benchmark ? benchmark.industryMax : metric.percentile75;

              const minFormatted = metric.isMonetary
                ? formatCurrency(minValue)
                : `${minValue.toLocaleString()}${metric.unit ? ` ${metric.unit}` : ''}`;

              const maxFormatted = metric.isMonetary
                ? formatCurrency(maxValue)
                : `${maxValue.toLocaleString()}${metric.unit ? ` ${metric.unit}` : ''}`;

              return [`Industry Range: ${minFormatted} - ${maxFormatted}`];
            } else if (datasetIndex === 1) { // Industry Average
              const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
              const avgValue = benchmark ? benchmark.nationalAverage : metric.average;
              const formattedValue = metric.isMonetary
                ? formatCurrency(avgValue)
                : `${avgValue.toLocaleString()}${metric.unit ? ` ${metric.unit}` : ''}`;
              return `Industry Average: ${formattedValue}`;
            } else if (datasetIndex === 2 && context.raw) { // Top Performer
              const benchmark = industryBenchmarks.find(b => b.metricName === metric.metricName);
              const topValue = benchmark?.topPerformerThreshold;
              if (topValue) {
                const formattedValue = metric.isMonetary
                  ? formatCurrency(topValue)
                  : `${topValue.toLocaleString()}${metric.unit ? ` ${metric.unit}` : ''}`;
                return `Top Performer Threshold: ${formattedValue}`;
              }
            }

            return '';
          }
        }
      }
    }
  };

  return (
    <div style={{ height: `${height}px`, width: '100%', position: 'relative' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default PerformanceRangeChart;
